import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AcademicCapIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import PMSwitch from "../../../utils/PMSwitch";
import { PMStepper } from "../../../utils/PMStepper";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { getDocumentAccess, updateDocumentAccess } from "../../../../api/hse/documents";
import {
  complianceErrorMessage,
  getComplianceOptions,
  updateDocumentsComplianceSettings,
} from "../../../../api/hse/documentCompliance";
import { promangToast } from "../../../../operationFeedback";
import { CompliancePeoplePicker } from "./CompliancePeoplePicker";

export const COMPLIANCE_DEFAULTS = {
  read_deadline_days: 14,
  read_max_postponements: 2,
  read_postponement_max_days: 7,
};

const AUDIENCE_TYPES = ["all", "positions", "none"];

/** Payload for PUT /hse_documents/:id/access or bulk settings (pure, unit tested). */
export const buildCompliancePayload = (form, { bulk = false, changeAccess = true, changeRequirement = true } = {}) => {
  const payload = {};
  if (!bulk || changeAccess) {
    payload.access_audience = {
      type: form.audienceType,
      position_ids: form.audienceType === "positions" ? form.positionIds : [],
    };
    if (bulk) {
      if (form.manualUserIds.length) payload.add_manual_user_ids = form.manualUserIds;
    } else {
      payload.manual_user_ids = form.manualUserIds;
    }
  }
  if (!bulk || changeRequirement) {
    payload.read_requirement = form.required ? "required" : "none";
    if (form.required) {
      payload.read_deadline_days = form.deadlineDays;
      payload.read_max_postponements = form.maxPostponements;
      payload.read_postponement_max_days = form.postponementDays;
    }
  }
  return payload;
};

/** Client-side validation mirroring the API (inline, before the request). */
export const validateComplianceForm = (form, { bulk = false, changeAccess = true, changeRequirement = true } = {}) => {
  const errors = {};
  if ((!bulk || changeAccess) && form.audienceType === "positions" && form.positionIds.length === 0) {
    errors.positions = "hseCompliance.dialog.error_positions";
  }
  if (bulk && !changeAccess && !changeRequirement) {
    errors.base = "hseCompliance.dialog.error_nothing";
  }
  return errors;
};

const formFromAccess = (data) => ({
  audienceType: AUDIENCE_TYPES.includes(data?.access_audience?.type) ? data.access_audience.type : "none",
  positionIds: (data?.access_audience?.position_ids || []).map(Number),
  manualUserIds: (data?.manual_user_ids || []).map(Number),
  required: (data?.read_requirement || "none") === "required",
  deadlineDays: data?.read_deadline_days || COMPLIANCE_DEFAULTS.read_deadline_days,
  maxPostponements: data?.read_max_postponements ?? COMPLIANCE_DEFAULTS.read_max_postponements,
  postponementDays: data?.read_postponement_max_days || COMPLIANCE_DEFAULTS.read_postponement_max_days,
});

/**
 * «Tilgang og lesekrav» — who can read an HMS document (rule + people + training) and whether
 * reading is required (deadline, postponements). One document (`documentId`) or many
 * (`documentIds`, bulk: each part is applied only when its switch is on).
 * Follows PROMANG_COMPONENTS.md §5.1 and §8.
 */
export const DocumentComplianceDialog = ({ open, onClose, documentId, documentIds, documentTitle, onSaved }) => {
  const { t } = useTranslation();
  const bulkIds = useMemo(() => (Array.isArray(documentIds) ? documentIds : []), [documentIds]);
  const bulk = !documentId && bulkIds.length > 0;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [options, setOptions] = useState(null);
  const [access, setAccess] = useState(null);
  const [form, setForm] = useState(() => formFromAccess({ access_audience: { type: "all" } }));
  const [changeAccess, setChangeAccess] = useState(true);
  const [changeRequirement, setChangeRequirement] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setErrors({});
    const requests = [getComplianceOptions()];
    if (!bulk && documentId) requests.push(getDocumentAccess(documentId));
    Promise.all(requests)
      .then(([optionsData, accessData]) => {
        if (cancelled) return;
        setOptions(optionsData);
        setAccess(accessData || null);
        setForm(formFromAccess(accessData || { access_audience: { type: "all" }, read_requirement: "none" }));
        setChangeAccess(true);
        setChangeRequirement(!bulk);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(complianceErrorMessage(error, t("hseCompliance.dialog.load_error")));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, documentId, bulk, t]);

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors({});
    setSaveError(null);
  };

  const positions = options?.positions || [];
  const people = options?.employees || [];
  const employeeCount = options?.employee_count || people.length;
  const audienceCount = useMemo(() => {
    if (form.audienceType === "all") return employeeCount;
    if (form.audienceType === "positions") {
      const ids = new Set(form.positionIds);
      return people.filter((person) => (person.position_ids || []).some((id) => ids.has(Number(id)))).length;
    }
    return 0;
  }, [form.audienceType, form.positionIds, people, employeeCount]);
  const reach = useMemo(() => {
    const ids = new Set(form.manualUserIds);
    if (form.audienceType === "all") return employeeCount;
    const positionIds = new Set(form.positionIds);
    people.forEach((person) => {
      if (form.audienceType === "positions" && (person.position_ids || []).some((id) => positionIds.has(Number(id)))) {
        ids.add(Number(person.id));
      }
    });
    return ids.size;
  }, [form, people, employeeCount]);

  const scope = { bulk, changeAccess, changeRequirement };

  const handleSave = async () => {
    const found = validateComplianceForm(form, scope);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildCompliancePayload(form, scope);
      if (bulk) {
        await updateDocumentsComplianceSettings(bulkIds, payload);
        promangToast.success(t("hseCompliance.dialog.saved_bulk", { count: bulkIds.length }));
      } else {
        await updateDocumentAccess(documentId, payload);
        promangToast.success(t("hseCompliance.dialog.saved"));
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      setSaveError(complianceErrorMessage(error, t("hseCompliance.dialog.save_error")));
    } finally {
      setSaving(false);
    }
  };

  const summary = access?.reader_summary;
  const trainingGuides = access?.training_guides || [];
  const errorCount = Object.keys(errors).length + (saveError ? 1 : 0);

  const accessSection = (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3" role="group" aria-label={t("hseCompliance.dialog.audience_title")}>
        {AUDIENCE_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className="pm-choice-card"
            aria-pressed={form.audienceType === type}
            disabled={bulk && !changeAccess}
            onClick={() => update({ audienceType: type })}
          >
            <span className="pm-choice-card__title">{t(`hseCompliance.audience.${type}`)}</span>
            <span className="pm-choice-card__desc">
              {type === "all"
                ? t("hseCompliance.audience.all_desc", { count: employeeCount })
                : t(`hseCompliance.audience.${type}_desc`)}
            </span>
          </button>
        ))}
      </div>

      {form.audienceType === "positions" ? (
        <div>
          <div className="pm-label">{t("hseCompliance.dialog.positions_label")}</div>
          {positions.length === 0 ? (
            <p className="pm-help">{t("hseCompliance.dialog.positions_empty")}</p>
          ) : (
            <div className="mt-1 flex flex-wrap gap-2">
              {positions.map((position) => {
                const pressed = form.positionIds.includes(Number(position.id));
                return (
                  <button
                    key={position.id}
                    type="button"
                    className="pm-chip"
                    aria-pressed={pressed}
                    disabled={bulk && !changeAccess}
                    onClick={() =>
                      update({
                        positionIds: pressed
                          ? form.positionIds.filter((id) => id !== Number(position.id))
                          : [...form.positionIds, Number(position.id)],
                      })
                    }
                  >
                    {position.name}
                    <span className="pm-chip__count">{position.employee_count}</span>
                  </button>
                );
              })}
            </div>
          )}
          {errors.positions ? <p className="pm-field-error" role="alert">{t(errors.positions)}</p> : null}
        </div>
      ) : null}

      <div>
        <div className="pm-label">
          {bulk
            ? t("hseCompliance.dialog.people_add_label")
            : form.audienceType === "none"
              ? t("hseCompliance.dialog.people_only_label")
              : t("hseCompliance.dialog.people_extra_label")}
        </div>
        <p className="pm-help mb-2">
          {bulk ? t("hseCompliance.dialog.people_add_help") : t("hseCompliance.dialog.people_help")}
        </p>
        <CompliancePeoplePicker
          idPrefix={bulk ? "compliance-bulk-people" : `compliance-people-${documentId}`}
          people={people}
          value={form.manualUserIds}
          disabled={bulk && !changeAccess}
          onChange={(ids) => update({ manualUserIds: ids })}
        />
        {errors.people ? <p className="pm-field-error" role="alert">{t(errors.people)}</p> : null}
      </div>

      <p className="text-[13px] text-[color:var(--pls-muted)]" aria-live="polite">
        {bulk
          ? t("hseCompliance.dialog.reach_bulk", { count: audienceCount })
          : t("hseCompliance.dialog.reach", { count: reach, total: employeeCount })}
      </p>
    </div>
  );

  const requirementSection = (
    <div className="flex flex-col gap-3">
      <PMSwitch
        variant="row"
        name="required"
        label={t("hseCompliance.dialog.required_label")}
        description={t("hseCompliance.dialog.required_desc")}
        checked={form.required}
        disabled={bulk && !changeRequirement}
        onChange={(event) => update({ required: event.target.checked })}
      />
      {form.required ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="pm-label" htmlFor="compliance-deadline">{t("hseCompliance.dialog.deadline_label")}</label>
            <PMStepper
              id="compliance-deadline"
              value={form.deadlineDays}
              min={1}
              max={365}
              suffix={t("hseCompliance.dialog.days")}
              disabled={bulk && !changeRequirement}
              onChange={(value) => update({ deadlineDays: value })}
            />
            <p className="pm-help">{t("hseCompliance.dialog.deadline_help")}</p>
          </div>
          <div>
            <label className="pm-label" htmlFor="compliance-postponements">{t("hseCompliance.dialog.postponements_label")}</label>
            <PMStepper
              id="compliance-postponements"
              value={form.maxPostponements}
              min={0}
              max={10}
              disabled={bulk && !changeRequirement}
              onChange={(value) => update({ maxPostponements: value })}
            />
            <p className="pm-help">{t("hseCompliance.dialog.postponements_help")}</p>
          </div>
          <div>
            <label className="pm-label" htmlFor="compliance-postpone-days">{t("hseCompliance.dialog.postpone_days_label")}</label>
            <PMStepper
              id="compliance-postpone-days"
              value={form.postponementDays}
              min={1}
              max={30}
              suffix={t("hseCompliance.dialog.days")}
              disabled={bulk && !changeRequirement || form.maxPostponements === 0}
              onChange={(value) => update({ postponementDays: value })}
            />
            <p className="pm-help">{t("hseCompliance.dialog.postpone_days_help")}</p>
          </div>
        </div>
      ) : (
        <p className="pm-help">{t("hseCompliance.dialog.not_required_help")}</p>
      )}
    </div>
  );

  const sectionCard = (enabled, setEnabled, label, children) => (
    <div className={`pm-surface-card pm-selectable-card${enabled ? " is-selected" : ""}`}>
      <div className="mb-3 flex items-center justify-end">
        <PMSwitch variant="row" name={label} label={t(label)} checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
      </div>
      {children}
    </div>
  );

  return (
    <PMDialog
      open={open}
      onClose={saving ? () => {} : onClose}
      size="6xl"
      panelClassName="sm:!max-h-[92dvh]"
      contentClassName="!overflow-hidden"
      eyebrow={t("hseCompliance.dialog.eyebrow")}
      title={bulk ? t("hseCompliance.dialog.title_bulk", { count: bulkIds.length }) : t("hseCompliance.dialog.title")}
      subtitle={bulk ? t("hseCompliance.dialog.subtitle_bulk") : documentTitle || t("hseCompliance.dialog.subtitle")}
    >
      {loading ? (
        <div className="p-8"><ResourceLoading /></div>
      ) : loadError ? (
        <div className="p-6">
          <div className="pm-alert pm-alert--bad" role="alert">
            <span className="pm-alert__icon" aria-hidden="true">!</span>
            <div>
              <div className="pm-alert__title">{t("hseCompliance.dialog.load_error")}</div>
              <div className="pm-alert__desc">{loadError}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6">
            <section className="pm-form-section">
              <div>
                <div className="pm-form-section__title">{t("hseCompliance.dialog.audience_title")}</div>
                <p className="pm-form-section__hint">{t("hseCompliance.dialog.audience_hint")}</p>
              </div>
              {bulk ? sectionCard(changeAccess, setChangeAccess, "hseCompliance.dialog.change_access", accessSection) : accessSection}
            </section>

            <section className="pm-form-section">
              <div>
                <div className="pm-form-section__title">{t("hseCompliance.dialog.requirement_title")}</div>
                <p className="pm-form-section__hint">{t("hseCompliance.dialog.requirement_hint")}</p>
              </div>
              {bulk
                ? sectionCard(changeRequirement, setChangeRequirement, "hseCompliance.dialog.change_requirement", requirementSection)
                : requirementSection}
            </section>

            {!bulk ? (
              <section className="pm-form-section">
                <div>
                  <div className="pm-form-section__title">{t("hseCompliance.dialog.training_title")}</div>
                  <p className="pm-form-section__hint">{t("hseCompliance.dialog.training_hint")}</p>
                </div>
                <div className="flex flex-col gap-3">
                  {trainingGuides.length ? (
                    <ul className="flex flex-col gap-2">
                      {trainingGuides.map((guide) => (
                        <li key={guide.id} className="flex items-center gap-2 text-[14px]">
                          <span className="pm-source-chip pm-source-chip--training">
                            <AcademicCapIcon aria-hidden="true" />
                            {t("hseCompliance.source.training")}
                          </span>
                          <span className="font-medium">{guide.title}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="pm-help">{t("hseCompliance.dialog.training_empty")}</p>
                  )}
                  {summary ? (
                    <div className="pm-surface-card grid grid-cols-2 gap-4 sm:grid-cols-4">
                      <div>
                        <div className="pm-fact__label">{t("hseCompliance.dialog.fact_readers")}</div>
                        <div className="pm-fact__value">{t("hseCompliance.dialog.fact_readers_value", { count: summary.reader_count, total: summary.employee_count })}</div>
                      </div>
                      <div>
                        <div className="pm-fact__label">{t("hseCompliance.status.acknowledged")}</div>
                        <div className="pm-fact__value">{summary.acknowledged_count}</div>
                      </div>
                      <div>
                        <div className="pm-fact__label">{t("hseCompliance.status.pending")}</div>
                        <div className="pm-fact__value">{summary.pending_count}</div>
                      </div>
                      <div>
                        <div className="pm-fact__label">{t("hseCompliance.status.overdue")}</div>
                        <div className="pm-fact__value">{summary.overdue_count}</div>
                      </div>
                    </div>
                  ) : null}
                  <Link className="pm-label-link text-[13px]" to="/dashboard/hse-ks/documents/employees" onClick={onClose}>
                    {t("hseCompliance.dialog.open_employees")}
                  </Link>
                </div>
              </section>
            ) : null}
          </div>

          <div className="pm-form-footer shrink-0">
            {errorCount ? (
              <div className="pm-form-footer__summary" role="alert">
                <span className="pm-form-footer__summary-icon" aria-hidden="true">!</span>
                <span>{saveError || t(errors.base || errors.positions || errors.people)}</span>
              </div>
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              <button type="button" className="pm-btn pm-btn--secondary" onClick={onClose} disabled={saving}>
                {t("Avbryt")}
              </button>
              <button type="button" className="pm-btn pm-btn--primary" onClick={handleSave} disabled={saving} aria-busy={saving || undefined}>
                {saving ? <span className="pm-btn__spinner" aria-hidden="true" /> : null}
                {bulk ? t("hseCompliance.dialog.save_bulk", { count: bulkIds.length }) : t("hseCompliance.dialog.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </PMDialog>
  );
};

export default DocumentComplianceDialog;
